import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useConsent } from './useConsent';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('visitor_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('visitor_session_id', sessionId);
  }
  return sessionId;
};

export const useVisitorTracking = () => {
  const location = useLocation();
  const { session } = useAuth();
  const { hasAnalyticsConsent } = useConsent();
  const sessionId = useRef(getSessionId());
  const heartbeatInterval = useRef<NodeJS.Timeout>();

  // Track page view - only if analytics consent is given
  const trackPageView = async (pagePath: string) => {
    if (!hasAnalyticsConsent()) {
      return;
    }
    
    try {
      await supabase.from('page_views').insert({
        session_id: sessionId.current,
        user_id: session?.user?.id || null,
        page_path: pagePath,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error('Error tracking page view:', error);
    }
  };

  // Track funnel events - only if analytics consent is given
  const trackFunnelEvent = async (eventType: string, eventData?: Record<string, any>) => {
    if (!hasAnalyticsConsent()) {
      return;
    }
    
    try {
      await supabase.from('funnel_events').insert({
        session_id: sessionId.current,
        user_id: session?.user?.id || null,
        event_type: eventType,
        event_data: eventData || null,
        page_path: location.pathname,
      });
    } catch (error) {
      console.error('Error tracking funnel event:', error);
    }
  };

  // Update active session - only if analytics consent is given
  const updateActiveSession = async () => {
    if (!hasAnalyticsConsent()) {
      return;
    }
    
    try {
      await supabase.rpc('track_active_session', {
        _session_id: sessionId.current,
        _page_path: location.pathname,
        _user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error('Error updating session:', error);
    }
  };

  // Mark session as inactive
  const markSessionInactive = async () => {
    try {
      await supabase.rpc('end_active_session', {
        _session_id: sessionId.current,
      });
    } catch (error) {
      console.error('Error marking session inactive:', error);
    }
  };

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname);
    updateActiveSession();
  }, [location.pathname, session]);

  // Heartbeat to keep session alive - only if analytics consent is given
  useEffect(() => {
    if (!hasAnalyticsConsent()) {
      // Clear any existing interval if consent is revoked
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
      return;
    }

    heartbeatInterval.current = setInterval(() => {
      updateActiveSession();
    }, 30000); // Every 30 seconds

    return () => {
      if (heartbeatInterval.current) {
        clearInterval(heartbeatInterval.current);
      }
    };
  }, [session, hasAnalyticsConsent()]);

  // Mark inactive on unmount/page leave
  useEffect(() => {
    const handleBeforeUnload = () => {
      markSessionInactive();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      markSessionInactive();
    };
  }, []);

  return { 
    sessionId: sessionId.current,
    trackFunnelEvent 
  };
};

